// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useState, type KeyboardEvent } from 'react'

import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { MarkdownEditor } from '@/components/ui/markdown-editor/markdown-editor'
import { Info, Loader2, Plus } from 'lucide-react'

import type { KbSchema } from '../types'
import type { KnowledgeBaseSettings } from '../hooks/useKnowledgeBaseSettings'
import { useItemDraft } from '../hooks/useItemDraft'
import { SCORE_MAX, SCORE_MIN, parseScore } from '../utils/validation'
import { SchemaFieldsEditor } from './SchemaFieldsEditor'
import { renderContentFromFields } from './fieldsRenderer'
import { parseGuidanceContent, riskLevel, RISK_SCORE, updateGuidanceContent, type RiskLevel } from './guidanceContent'

export interface AddItemFormSubmission {
  draft: {
    content: string
    score: number | null
    sectionType?: string
    sectionLabel?: string
    fields?: Record<string, string> | null
  }
  saveAndNext: boolean
}

interface AddItemFormProps {
  disabled: boolean
  saving: boolean
  settings: KnowledgeBaseSettings
  schema?: KbSchema | null
  onSubmit: (submission: AddItemFormSubmission) => Promise<void>
  onError: (message: string) => void
}

export function AddItemForm({
  disabled,
  saving,
  settings,
  schema,
  onSubmit,
  onError,
}: AddItemFormProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const draft = useItemDraft({ score: '3' })

  const scoreValue = parseScore(draft.score)
  const requiresScore = settings.mode === 'findings'
  const showSectionFields = settings.mode === 'report'
  const useSchema = !!schema && schema.fields.length > 0
  const guidance = parseGuidanceContent(draft.content)

  const fieldsHaveAnyValue = useSchema
    ? Object.values(draft.fields).some(v => v.trim().length > 0)
    : false
  const missingRequiredField = useSchema && schema
    ? schema.fields.find(f => f.required && !(draft.fields[f.key] ?? '').trim())
    : undefined

  const canSubmit =
    (useSchema
      ? fieldsHaveAnyValue && !missingRequiredField
      : requiresScore
        ? guidance.title.length > 0 && guidance.issue.length > 0
        : draft.content.trim().length > 0) &&
    (!requiresScore || scoreValue !== null) &&
    !disabled

  const resetDraft = useCallback(() => {
    draft.reset({ score: '3' })
  }, [draft])

  const submit = useCallback(
    async (saveAndNext: boolean) => {
      if (!canSubmit) {
        if (missingRequiredField) {
          onError(`${missingRequiredField.label} is required.`)
        } else if (requiresScore && scoreValue === null) {
          onError(`${settings.scoreLabel} must be a whole number between ${SCORE_MIN} and ${SCORE_MAX}.`)
        } else {
          onError('Please complete all required fields before saving the guidance item.')
        }
        return
      }

      const content = useSchema && schema
        ? renderContentFromFields(schema, draft.fields)
        : draft.content.trim()

      try {
        await onSubmit({
          draft: {
            content,
            score: requiresScore ? scoreValue : null,
            sectionType: showSectionFields ? draft.sectionType.trim() || undefined : undefined,
            sectionLabel: showSectionFields ? draft.sectionLabel.trim() || undefined : undefined,
            fields: useSchema ? draft.fields : null,
          },
          saveAndNext,
        })

        resetDraft()
        if (!saveAndNext) setIsOpen(false)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to add item'
        onError(message)
      }
    },
    [
      canSubmit,
      missingRequiredField,
      requiresScore,
      showSectionFields,
      useSchema,
      schema,
      draft.content,
      draft.fields,
      draft.sectionType,
      draft.sectionLabel,
      scoreValue,
      resetDraft,
      onSubmit,
      onError,
      settings.scoreLabel,
    ],
  )

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault()
      if (canSubmit && !saving) {
        void submit(true)
      }
    }
  }, [canSubmit, saving, submit])

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={isOpen ? 'col-span-full' : ''}>
      <div className={isOpen ? 'rounded-lg border border-border p-4' : 'flex justify-end'}>
        <CollapsibleTrigger asChild>
          <Button variant={isOpen ? 'ghost' : 'default'} className={isOpen ? 'h-auto w-full justify-between p-0 text-left hover:bg-transparent' : ''} data-testid="kb-add-item">
            <span className="min-w-0 font-medium">
              {isOpen && <span className="block text-base font-semibold">Add Guidance Item</span>}
              {!isOpen && 'Add Guidance Item'}
              {isOpen && <span className="block text-sm font-normal text-muted-foreground">Create local guidance that appears in analysis prompts for reviewers.</span>}
            </span>
            <Plus className={`h-4 w-4 shrink-0 ${isOpen ? 'ml-2 rotate-45' : 'mr-2 order-first'}`} />
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="pt-4 space-y-4">
          {useSchema && schema ? (
            <div className={disabled ? 'pointer-events-none opacity-60' : ''} onKeyDown={handleKeyDown}>
              <SchemaFieldsEditor
                schema={schema}
                values={draft.fields}
                disabled={disabled || saving}
                onChange={draft.updateField}
              />
            </div>
          ) : requiresScore ? (
            <div className="space-y-3" onKeyDown={handleKeyDown}>
              <Input
                value={guidance.title}
                onChange={event => draft.update({ content: updateGuidanceContent(draft.content, event.target.value, guidance.issue) })}
                placeholder="Title — e.g. Governing Law Must Be State Law"
                aria-label="Guidance title"
                className="h-[38px] rounded-lg border-slate-300 bg-white px-3 text-sm font-medium text-slate-900"
              />
              <Textarea
                value={guidance.issue}
                onChange={event => draft.update({ content: updateGuidanceContent(draft.content, guidance.title, event.target.value) })}
                placeholder="Issue — what should reviewers look for?"
                aria-label="Guidance issue"
                rows={3}
                className="rounded-lg border-slate-300 bg-white px-3 py-2.5 text-[13.5px] leading-[1.55] text-slate-700"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="new-content">Guidance content *</Label>
              <div className={disabled ? 'pointer-events-none opacity-60' : ''}>
                <MarkdownEditor
                  value={draft.content}
                  onChange={(value: string) => draft.update({ content: value })}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
          )}

          {showSectionFields ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="new-section-type" className="flex items-center gap-1 text-sm font-medium">
                  Section Type
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-sm leading-relaxed">
                        Machine-readable identifier for grouping (e.g., cost_benefit, negotiation).
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Input
                  id="new-section-type"
                  value={draft.sectionType}
                  placeholder="e.g., cost_benefit"
                  onChange={event => draft.update({ sectionType: event.target.value })}
                  disabled={disabled || saving}
                />
                <p className="text-sm text-slate-600">Used to group items in the report output.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-section-label" className="flex items-center gap-1 text-sm font-medium">
                  Section Label
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-sm leading-relaxed">
                        Human-readable label for this section in the report.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Input
                  id="new-section-label"
                  value={draft.sectionLabel}
                  placeholder="e.g., Cost-Benefit Analysis"
                  onChange={event => draft.update({ sectionLabel: event.target.value })}
                  disabled={disabled || saving}
                />
                <p className="text-sm text-slate-600">Displayed as the section header in reports.</p>
              </div>
            </div>
          ) : requiresScore ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[12.5px] font-medium text-slate-600">Risk level</span>
              <div className="flex gap-1 rounded-lg bg-slate-100 p-[3px]" data-testid="kb-risk-score">
                {(['low', 'medium', 'high'] as RiskLevel[]).map(risk => (
                  <button
                    key={risk}
                    type="button"
                    onClick={() => draft.update({ score: String(RISK_SCORE[risk]) })}
                    className={`h-7 rounded-md px-3.5 text-[12.5px] capitalize ${riskLevel(scoreValue) === risk
                      ? 'bg-white font-semibold text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,.14)]'
                      : 'font-medium text-slate-500 hover:text-slate-900'
                    }`}
                  >
                    {risk}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            {requiresScore && (
              <Button type="button" variant="outline" size="sm" onClick={() => setIsOpen(false)} disabled={saving}>
                Cancel
              </Button>
            )}
            <Button
              data-testid="kb-add-button"
              onClick={() => void submit(false)}
              size="sm"
              variant={requiresScore ? 'default' : 'outline'}
              disabled={!canSubmit || saving}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Item
            </Button>
            {!requiresScore && (
              <Button
                onClick={() => void submit(true)}
                size="sm"
                disabled={!canSubmit || saving}
                data-testid="kb-add-submit"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Save & Next
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
